/**
 * SetPassphraseScreen — funzione Premium.
 *
 * Permette a un utente Premium di sostituire il PIN di 6 cifre con una
 * PASSPHRASE alfanumerica come segreto che protegge il backup remoto delle
 * 24 parole. Stessa work-factor (Argon2id MODERATE): il guadagno e' l'entropia
 * molto piu' alta → brute-force offline impraticabile.
 *
 * Flusso (identico a ChangePin, ma il nuovo segreto e' una passphrase):
 *   1. verifica il segreto ATTUALE (PIN o passphrase) → fetchBackup + decrypt
 *   2. cifra il mnemonic con la nuova passphrase
 *   3. setBackup sul server
 *   4. salva unlockMode='passphrase' su questo device (default input al prossimo
 *      sblocco). Da quel momento si sblocca con la passphrase.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Frame, Header, Footer, Btn, Box, FieldLabel, Cursor,
} from '../components/Frame';
import { useStore } from '../lib/store';
import { callFn } from '../lib/firebase';
import {
  isValidPassphrase,
  passphraseStrength,
  encryptMnemonic,
  decryptMnemonic,
  saveLastSigil,
  saveUnlockMode,
} from '../lib/pinCrypto';
import { isValid } from '../lib/mnemonic';

const STRENGTH_LABEL = {
  weak: { txt: 'debole', cls: 'text-amber' },
  ok: { txt: 'buona', cls: 'text-ink' },
  strong: { txt: 'forte', cls: 'text-ink text-glow' },
};

export default function SetPassphraseScreen() {
  const navigate = useNavigate();
  const { sigil, mnemonic, premium } = useStore();
  const sigilLower = (sigil || '').toLowerCase();

  const [current, setCurrent] = useState('');
  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [stage, setStage] = useState('idle');
  const [success, setSuccess] = useState(false);

  const strength = pass ? passphraseStrength(pass) : null;

  async function onSet() {
    setErr(null);
    if (current.length < 6) { setErr('inserisci il PIN o la passphrase attuale'); return; }
    if (!isValidPassphrase(pass)) {
      setErr('la passphrase deve avere almeno 8 caratteri (≥12 se usi solo lettere).');
      return;
    }
    if (pass !== confirm) { setErr('le due passphrase non corrispondono'); return; }
    if (current === pass) { setErr('la nuova passphrase deve essere diversa dal segreto attuale'); return; }
    if (!sigil || !mnemonic) { setErr('sessione non valida. Sblocca prima.'); return; }

    setBusy(true);
    try {
      // 1. Verifica segreto attuale: fetch dal server (rate-limit) + decrypt.
      setStage('verifying');
      const fetchRes = await callFn('fetchBackup')({ sigilLower });
      const d = fetchRes?.data || {};
      if (d.rateLimited) {
        setErr(`Troppi tentativi. Riprova tra ${d.retryAfterMinutes || 60} minuti.`);
        return;
      }
      if (!d.ok || !d.found) {
        setErr('Backup non trovato sul server. Per impostare la passphrase serve recovery con 24 parole.');
        return;
      }
      const tryDecrypt = await decryptMnemonic({
        pin: current, sigilLower,
        blob: d.blob, salt: d.salt, nonce: d.nonce, argon2: d.argon2 || null,
      });
      if (!tryDecrypt || !isValid(tryDecrypt)) {
        setErr('PIN/passphrase attuale sbagliato');
        return;
      }

      // 2. Cifra con la nuova passphrase
      setStage('encrypting');
      const bundle = await encryptMnemonic({ pin: pass, sigilLower, mnemonic });

      // 3. Upload al server
      setStage('uploading');
      const res = await callFn('setBackup')({
        sigilLower,
        blob: bundle.blob, salt: bundle.salt, nonce: bundle.nonce, argon2: bundle.argon2,
      });
      if (!res?.data?.ok) {
        throw new Error(`server: ${res?.data?.error || 'sconosciuto'}`);
      }

      // 4. Da ora questo device sblocca con la passphrase
      saveLastSigil(sigil);
      saveUnlockMode(sigil, 'passphrase');

      setSuccess(true);
      setStage('done');
      setTimeout(() => navigate('/inbox'), 1700);
    } catch (e) {
      console.error('set passphrase failed', e);
      setErr(e?.message || 'errore sconosciuto');
      setStage('idle');
    } finally {
      setBusy(false);
    }
  }

  // ── Gate Premium ────────────────────────────────────────────────────────
  if (!premium) {
    return (
      <Frame statusLeft="PASSPHRASE" statusRight="5G">
        <Header
          title="// PASSPHRASE DI SBLOCCO"
          crumb="◀ impostazioni"
          onCrumbClick={() => navigate('/impostazioni')}
        />
        <Box variant="warn" className="text-[15px]">
          ⭐ La passphrase alfanumerica è una funzione <b>Premium</b>.
        </Box>
        <p className="text-[15px] text-ink-dim mt-3">
          Sostituisce il PIN di 6 cifre con un segreto molto più robusto
          (8+ caratteri) per proteggere il backup delle tue 24 parole.
        </p>
        <Footer>
          <Btn variant="primary" onClick={() => navigate('/premium')}>Scopri Premium ▶</Btn>
        </Footer>
      </Frame>
    );
  }

  if (success) {
    return (
      <Frame statusLeft="PASSPHRASE_SET" statusRight="5G">
        <Header title="// PASSPHRASE IMPOSTATA" />
        <Box variant="success" className="text-[16px]">
          ✓ Passphrase impostata. Da ora sblocchi con la passphrase.
          Le 24 parole restano la tua ancora di recupero.
        </Box>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="SET_PASSPHRASE" statusRight="5G">
      <Header
        title="// IMPOSTA PASSPHRASE"
        crumb="◀ impostazioni"
        onCrumbClick={() => navigate('/impostazioni')}
      />

      <p className="text-[15px] text-ink-dim mb-4">
        sostituisce il PIN con una passphrase alfanumerica. Più lunga e varia
        è, più è robusta. Se la dimentichi, recuperi sempre con le 24 parole.
      </p>

      <div className="space-y-3">
        <div>
          <FieldLabel>&gt; PIN O PASSPHRASE ATTUALE</FieldLabel>
          <input
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-2 rounded-lg focus:border-ink focus:outline-none"
            placeholder="segreto attuale"
          />
        </div>

        <div>
          <FieldLabel>&gt; NUOVA PASSPHRASE</FieldLabel>
          <input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-2 rounded-lg focus:border-ink focus:outline-none"
            placeholder="almeno 8 caratteri"
          />
          {strength && (
            <p className="text-[12px] mt-1">
              robustezza:{' '}
              {strength === 'invalid'
                ? <span className="text-amber">troppo corta</span>
                : <span className={STRENGTH_LABEL[strength].cls}>{STRENGTH_LABEL[strength].txt}</span>}
            </p>
          )}
        </div>

        <div>
          <FieldLabel>&gt; RIPETI NUOVA PASSPHRASE</FieldLabel>
          <input
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-2 rounded-lg focus:border-ink focus:outline-none"
            placeholder="ripeti"
          />
        </div>

        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-[13px] text-ink-dim hover:text-ink"
        >
          {show ? '🙈 nascondi' : '👁 mostra'} i campi
        </button>

        {err && (
          <Box variant="danger" className="text-[16px]">
            <span className="text-red-500">✕</span> {err}
          </Box>
        )}

        {busy && (
          <Box variant="warn" className="text-[16px]">
            {stage === 'verifying' && (<>verifica segreto attuale<Cursor /></>)}
            {stage === 'encrypting' && (<>cifratura<Cursor /></>)}
            {stage === 'uploading' && (<>aggiornamento server<Cursor /></>)}
          </Box>
        )}
      </div>

      <Footer>
        <Btn
          variant="primary"
          onClick={onSet}
          disabled={busy || current.length < 6 || !isValidPassphrase(pass) || pass !== confirm}
        >
          {busy ? '...' : 'Imposta passphrase ▶'}
        </Btn>
      </Footer>
    </Frame>
  );
}
